import React from 'react';
import { Phone, MessageSquare, Globe, ArrowRight, Share2, Monitor, TrendingUp } from 'lucide-react';
import { Link } from 'react-router-dom';
import { CircuitPaths, CharReveal, TiltCard, Reveal } from './TerminalEffects';

const SERVICES = [
  { icon: Phone, title: 'AI Voice Agents', description: 'Never miss a call again. Our AI voice agents handle customer calls 24/7 with natural conversations.', features: ['24/7 Availability', 'Natural Conversations', 'Multi-language', 'Call Analytics'], accent: '#00FFD1' },
  { icon: MessageSquare, title: 'AI Chatbots', description: 'Engage customers instantly with intelligent chatbots that understand context and guide users.', features: ['Instant Responses', 'Lead Qualification', 'Context Aware', 'CRM Integration'], accent: '#C4956A' },
  { icon: TrendingUp, title: 'Lead Pipeline', description: 'Turn Facebook leads into booked appointments. AI qualifies, follows up, and books—automatically.', features: ['2s Response Time', 'Auto Qualification', 'Smart Follow-ups', '3x More Bookings'], accent: '#00e5b4', link: '/lead-pipeline' },
  { icon: Monitor, title: 'Website Development', description: 'Custom, high-performance websites built to convert visitors into customers and grow your brand.', features: ['Custom Design', 'Mobile Optimised', 'SEO Ready', 'Fast Performance'], accent: '#C4956A', contact: true },
  { icon: Share2, title: 'Social Media Management', description: 'Automate your social presence with AI-driven content scheduling, engagement, and growth strategies.', features: ['Content Scheduling', 'Engagement Tracking', 'Growth Strategy', 'Multi-platform'], accent: '#00FFD1', contact: true },
];

const ServiceCard = ({ service, index }) => {
  const Icon = service.icon;
  const CardContent = (
    <div className="bg-[#1e1e1e] rounded-2xl p-8 flex flex-col h-full relative overflow-hidden"
      data-testid={`service-card-${index}`}>
      <div className="absolute top-0 right-0 w-32 h-32 opacity-[0.06] rounded-bl-full" style={{ backgroundColor: service.accent }} />
      <div className="mb-5">
        <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: `${service.accent}20` }}>
          <Icon size={22} style={{ color: service.accent }} />
        </div>
      </div>
      <h3 className="text-xl font-semibold text-[#F5F0E0] mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>{service.title}</h3>
      <p className="text-sm text-[#F5F0E0]/60 mb-5 leading-relaxed flex-1" style={{ fontFamily: "'Manrope', sans-serif" }}>{service.description}</p>
      <ul className="space-y-2">
        {service.features.map((f) => (
          <li key={f} className="flex items-center gap-2.5 text-sm text-[#F5F0E0]/70">
            <div className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: service.accent }} />{f}
          </li>
        ))}
      </ul>
      {service.link && (
        <div className="mt-5 pt-4 border-t border-[#F5F0E0]/10">
          <div className="flex items-center gap-2 text-sm font-medium" style={{ color: service.accent }}>
            Learn More <ArrowRight size={16} />
          </div>
        </div>
      )}
      {service.contact && (
        <div className="mt-5 pt-4 border-t border-[#F5F0E0]/10">
          <p className="text-xs text-[#F5F0E0]/40 mb-2">Interested? Get in touch:</p>
          <div className="flex gap-3">
            <a href="https://wa.me/447776752870" target="_blank" rel="noopener noreferrer" className="text-xs text-[#00FFD1] hover:underline">WhatsApp</a>
            <span className="text-[#F5F0E0]/20">|</span>
            <a href="mailto:info@hiboticsai.com" className="text-xs text-[#00FFD1] hover:underline">info@hiboticsai.com</a>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <TiltCard accentColor={service.accent} className="h-full">
      {service.link ? (
        <Link to={service.link} className="block h-full cursor-pointer hover:scale-[1.02] transition-transform">
          {CardContent}
        </Link>
      ) : (
        CardContent
      )}
    </TiltCard>
  );
};

export const ServicesSection = () => (
  <section id="services" className="relative py-28 bg-[#F5F0E0] overflow-hidden" data-testid="services-section">
    <CircuitPaths />
    <div className="max-w-[1200px] mx-auto px-6 lg:px-8 relative z-10">
      <Reveal>
        <div className="mb-20 max-w-[600px]">
          <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#C4956A] mb-4">Our Services</p>
          <CharReveal text="AI Solutions That Work for You" tag="h2"
            className="text-4xl sm:text-5xl lg:text-6xl font-semibold text-[#2A2A2A] leading-[1.05] mb-6" />
        </div>
      </Reveal>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {SERVICES.slice(0, 3).map((s, i) => (
          <Reveal key={s.title} delay={i * 150}><ServiceCard service={s} index={i} /></Reveal>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-4xl mx-auto">
        {SERVICES.slice(3).map((s, i) => (
          <Reveal key={s.title} delay={(i + 3) * 150}><ServiceCard service={s} index={i + 3} /></Reveal>
        ))}
      </div>
      <Reveal delay={100}>
        <div className="mt-16 flex flex-col sm:flex-row items-start gap-4">
          <Link to="/onboarding"
            className="inline-flex items-center gap-2.5 bg-[#2A2A2A] text-white px-7 py-3 rounded-full text-xs font-mono uppercase tracking-[0.12em] hover:scale-105 hover:shadow-[0_0_30px_rgba(0,255,209,0.15)] transition-all duration-300 group"
            data-testid="services-cta">
            Design Your Hi-Bot <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
          </Link>
          <span className="text-xs font-mono text-[#2A2A2A]/40 uppercase tracking-wider self-center">No credit card required</span>
        </div>
      </Reveal>
    </div>
  </section>
);
